import { Loader } from "@/components/Loader";
import { useUserTimezone } from "@/hooks/useUserTimezone";

import type { InsightsFilters } from "./insightsFilters";

import { useInsightsOverview } from "../../hooks/useInsightsOverview";
import { formatMetric } from "./formatMetric";
import { DATE_RANGE_OPTIONS } from "./insightsFilters";
import { BestDaysToPost } from "./overview/BestDaysToPost";
import { FormatBreakdown } from "./overview/FormatBreakdown";
import { Highlights } from "./overview/Highlights";
import { InteractionsBreakdown } from "./overview/InteractionsBreakdown";
import { MetricCard } from "./overview/MetricCard";
import { TopPosts } from "./overview/TopPosts";
import { ViewsChart } from "./overview/ViewsChart";

const PANEL_CARD = "rounded-none border-0";

export function InsightsOverview({
  filters,
  onSeeAllPosts,
}: {
  filters: InsightsFilters;
  onSeeAllPosts: () => void;
}) {
  const { timezone } = useUserTimezone();
  const { data, isLoading } = useInsightsOverview({ ...filters, timezone });

  if (isLoading || !data) return <Loader className="py-20" />;

  const rangeLabel =
    DATE_RANGE_OPTIONS.find(({ days }) => days === filters.days)?.label ??
    `Last ${filters.days} days`;

  if (data.postCount === 0) {
    return (
      <p className="p-3xl text-center text-sm text-text-tertiary">
        No posts were published in the {rangeLabel.toLowerCase()}.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2xl p-xl">
      <h2 className="text-display-xs font-semibold text-text-primary">
        Your posts were seen {formatMetric(data.totals.views)} times
      </h2>

      <div className="grid items-start gap-lg lg:grid-cols-[3fr_2fr]">
        <section className="overflow-hidden rounded-2xl border border-border-secondary bg-bg-primary">
          <div className="grid divide-y divide-border-secondary border-b border-border-secondary md:grid-cols-3 md:divide-x md:divide-y-0">
            <MetricCard
              className="bg-bg-secondary"
              current={data.totals.views}
              description="Times your posts appeared on screen"
              label="Views"
              previous={data.previousTotals.views}
            />
            <MetricCard
              current={data.totals.reach}
              description="Accounts that saw your posts"
              label="People reached"
              previous={data.previousTotals.reach}
            />
            <MetricCard
              current={data.totals.interactions}
              description="Likes, comments, shares and saves"
              label="Interactions"
              previous={data.previousTotals.interactions}
            />
          </div>
          <ViewsChart
            daily={data.daily}
            days={filters.days}
            subtitle={`${data.postCount} ${data.postCount === 1 ? "post" : "posts"} · ${rangeLabel}`}
            timezone={timezone}
          />
        </section>
        <Highlights
          bestPost={data.topPosts[0]}
          bestWeekday={data.bestWeekday}
        />
      </div>

      <TopPosts
        averageViews={data.averageViews}
        onSeeAllPosts={onSeeAllPosts}
        posts={data.topPosts}
        rangeLabel={rangeLabel}
      />

      <div className="grid divide-y divide-border-secondary overflow-hidden rounded-2xl border border-border-secondary bg-bg-primary lg:grid-cols-3 lg:divide-x lg:divide-y-0">
        <FormatBreakdown className={PANEL_CARD} formats={data.formats} />
        <BestDaysToPost className={PANEL_CARD} weekdays={data.weekdays} />
        <InteractionsBreakdown
          className={PANEL_CARD}
          interactions={data.interactions}
        />
      </div>
    </div>
  );
}
